// src/pages/public/Home.jsx
import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { 
  TrendingUp, 
  Shield, 
  Clock, 
  Users, 
  DollarSign, 
  ArrowRight,
  Star,
  Check,
  Award,
  Globe,
  Lock,
  Zap,
  BarChart3,
  Wallet,
} from 'lucide-react'
import { api } from '../../api/client'
import { endpoints } from '../../api/endpoints'
import CountdownTimer from '../../components/widgets/CountdownTimer'
import PackagePreview from '../../components/PackagePreview'
import TestimonialCard from '../../components/TestimonialCard'
import TrustBadges from '../../components/TrustBadges'
import AnimatedCounter from '../../components/widgets/AnimatedCounter'

const Home = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0)
  const [displayText, setDisplayText] = useState('')
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isDeleting, setIsDeleting] = useState(false)
  const [loopNum, setLoopNum] = useState(0)

  const texts = ['Smart Investing Made Simple', 'AI-Powered Returns', 'Secure & Transparent']

  // Typewriter effect
  useEffect(() => {
    const currentText = texts[loopNum % texts.length]
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        setDisplayText(currentText.substring(0, currentIndex + 1))
        setCurrentIndex(prev => prev + 1)
        
        if (currentIndex === currentText.length) {
          setTimeout(() => setIsDeleting(true), 2000)
        }
      } else {
        setDisplayText(currentText.substring(0, currentIndex - 1))
        setCurrentIndex(prev => prev - 1)
        
        if (currentIndex === 0) {
          setIsDeleting(false)
          setLoopNum(prev => prev + 1)
        }
      }
    }, isDeleting ? 50 : 100)

    return () => clearTimeout(timeout)
  }, [currentIndex, isDeleting, loopNum, texts])

  // Fetch platform statistics
  const { data: platformStats } = useQuery({
    queryKey: ['platform-stats'],
    queryFn: () => api.get(endpoints.public.stats).then(res => res.data),
    staleTime: 5 * 60 * 1000,
  })

  // Fetch services
  const { data: services } = useQuery({
    queryKey: ['public-services'],
    queryFn: () => api.get(endpoints.public.services).then(res => res.data),
    staleTime: 5 * 60 * 1000,
  })

  // Fetch testimonials
  const { data: testimonials } = useQuery({
    queryKey: ['public-testimonials'],
    queryFn: () => api.get(endpoints.public.testimonials).then(res => res.data),
    staleTime: 10 * 60 * 1000,
  })

  // Auto-rotate testimonials
  useEffect(() => {
    if (testimonials?.testimonials?.length > 1) {
      const interval = setInterval(() => {
        setActiveTestimonial((prev) => 
          prev === testimonials.testimonials.length - 1 ? 0 : prev + 1
        )
      }, 5000)
      return () => clearInterval(interval)
    }
  }, [testimonials])

  const features = [
    {
      icon: Shield,
      title: "Bank-Level Security",
      description: "256-bit SSL encryption, 2FA protection, and cold wallet storage for maximum security",
      color: "red",
      stats: "99.9% Uptime"
    },
    {
      icon: Zap,
      title: "Lightning Fast",
      description: "Instant deposits and withdrawals with real-time processing and 24/7 availability",
      color: "green",
      stats: "<30s Processing"
    },
    {
      icon: BarChart3,
      title: "AI-Powered Analytics",
      description: "Smart investment algorithms and real-time market analysis for optimal returns",
      color: "blue",
      stats: "AI Optimized"
    },
    {
      icon: Wallet,
      title: "Multi-Currency",
      description: "Support for multiple cryptocurrencies and fiat currencies with competitive rates",
      color: "purple",
      stats: "10+ Currencies"
    }
  ]

  // const stats = platformStats || {
    const stats = {
    totalUsers: 15420,
    totalInvested: 2580000 ,
    totalWithdrawn: 1950000,
    avgROI: 18.5,
    activePackages: 8720
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900/20">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 dark:from-blue-900 dark:via-purple-900 dark:to-indigo-900 text-white overflow-hidden pt-20">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute top-40 right-20 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-1/2 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative container mx-auto px-4 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Hero Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-6"
            >
              <div className="space-y-4">
                {/* Trust Badge */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 border border-white/20"
                >
                  <Award className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm font-medium">Trusted by 15,420+ Investors</span>
                </motion.div>

                <h1 className="text-4xl lg:text-6xl font-bold leading-tight min-h-[120px] lg:min-h-[140px]">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                    {displayText}
                  </span>
                  <span className="ml-1 animate-pulse">|</span>
                </h1>
                <p className="text-lg text-blue-100 max-w-lg leading-relaxed">
                  Join the future of investment with AI-powered returns, institutional-grade security, 
                  and transparent blockchain technology.
                </p>
              </div>

              {/* Stats Bar */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="grid grid-cols-2 lg:grid-cols-4 gap-4 p-4 bg-white/10 backdrop-blur-lg rounded-xl border border-white/20"
              >
                <div className="text-center">
                  <div className="text-xl lg:text-2xl font-bold text-yellow-400">
                    <AnimatedCounter value={(stats.totalUsers / 1000).toFixed(1)} suffix="K+" />
                  </div>
                  <div className="text-xs text-blue-100 mt-1">Active Users</div>
                </div>
                <div className="text-center">
                  <div className="text-xl lg:text-2xl font-bold text-green-400">
                    ${(stats.totalInvested / 1000000).toFixed(1)}M+
                  </div>
                  <div className="text-xs text-blue-100 mt-1">Total Invested</div>
                </div>
                <div className="text-center">
                  <div className="text-xl lg:text-2xl font-bold text-emerald-400">
                    ${(stats.totalWithdrawn / 1000000).toFixed(1)}M+
                  </div>
                  <div className="text-xs text-blue-100 mt-1">Total Withdrawn</div>
                </div>
                <div className="text-center">
                  <div className="text-xl lg:text-2xl font-bold text-orange-400">
                    <AnimatedCounter value={stats.avgROI} suffix="%" />
                  </div>
                  <div className="text-xs text-blue-100 mt-1">Avg. Daily ROI</div>
                </div>
              </motion.div>

              {/* CTA Buttons */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className="flex flex-col 4xl:flex-row gap-4"
              >
                <Link
                  to="https://udash.yestimeearn.com/auth/register"
                  className="group bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-gray-900 px-8 py-4 rounded-xl font-semibold text-lg flex items-center justify-center gap-3 transition-all transform hover:scale-105 shadow-lg hover:shadow-yellow-500/25 w-full sm:w-auto text-center"
                >
                  <TrendingUp className="w-5 h-5" />
                  Start Investing Free
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>

              {/* Trust Badges */}
              <TrustBadges />
            </motion.div>

            {/* Hero Visual */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 border border-white/20 shadow-xl">
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm text-blue-200 font-medium">Current Package</div>
                      <div className="text-xl font-bold">Premium Plus</div>
                    </div>
                    <motion.div
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs font-bold"
                    >
                      ⚡ Hot Deal
                    </motion.div>
                  </div>

                  {/* ROI Section */}
                  <div className="text-center py-2">
                    <div className="text-sm text-blue-200 mb-1">Daily ROI</div>
                    <div className="text-2xl font-bold text-green-400">2.8% - 3.5%</div>
                  </div>
                  
                  {/* Investment Details */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/10 rounded-lg p-3 text-center">
                      <div className="text-xs text-blue-200 mb-1">Investment</div>
                      <div className="text-base font-bold">$25,000</div>
                    </div>
                    <div className="bg-white/10 rounded-lg p-3 text-center">
                      <div className="text-xs text-blue-200 mb-1">Returns Cap</div>
                      <div className="text-base font-bold text-green-400">3x</div>
                    </div>
                  </div>

                  {/* Countdown Timer */}
                  <div className="pt-2">
                    <div className="text-xs text-blue-200 text-center mb-2">Next ROI in</div>
                    <CountdownTimer />
                  </div>
                  
                  {/* Progress Chart */}
                  <div className="pt-3">
                    <div className="flex justify-between text-xs text-blue-200 mb-1">
                      <span>Portfolio Growth</span>
                      <span>+28.5%</span>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-1.5">
                      <div 
                        className="bg-gradient-to-r from-green-400 to-emerald-400 h-1.5 rounded-full transition-all duration-1000"
                        style={{ width: '70%' }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white dark:bg-gray-800 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-600 to-purple-600"></div>
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Why Investors Choose{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                YesTimeEarn
              </span>
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Experience institutional-grade investment features with user-friendly simplicity
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group relative"
              >
                <div className="relative bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-200 dark:border-gray-700 group-hover:scale-105">
                  {/* Feature Icon */}
                  <div className={`bg-gradient-to-r from-${feature.color}-500 to-${feature.color}-600 w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <feature.icon className="w-6 h-6 text-white" />
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 leading-relaxed">
                    {feature.description}
                  </p>
                  
                  {/* Stats */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">
                      {feature.stats}
                    </span>
                    <Check className="w-4 h-4 text-green-500" />
                  </div>

                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600/5 to-purple-600/5 rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Packages Preview */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900/20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Premium{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Investment Packages
              </span>
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Choose from our carefully curated investment packages designed for maximum returns
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {services?.services?.slice(0, 3).map((pkg, index) => (
              <motion.div
                key={pkg.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <PackagePreview pkg={pkg} />
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link
              to="./services"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition-all transform hover:scale-105 shadow-lg hover:shadow-blue-500/25"
            >
              Explore All Packages
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Trusted by{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
                Thousands
              </span>
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              See what our investors are saying about their experience
            </p>
          </motion.div>

          {/* Testimonials Carousel */}
          <div className="max-w-6xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="grid md:grid-cols-3 gap-6"
              >
                {testimonials?.testimonials?.slice(activeTestimonial, activeTestimonial + 3).map((testimonial, index) => (
                  <TestimonialCard key={testimonial.id} testimonial={testimonial} />
                ))}
              </motion.div>
            </AnimatePresence>

            {/* Carousel Indicators */}
            {testimonials?.testimonials?.length > 3 && (
              <div className="flex justify-center space-x-2 mt-6">
                {[...Array(Math.ceil(testimonials.testimonials.length / 3))].map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveTestimonial(index * 3)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      activeTestimonial === index * 3
                        ? 'bg-blue-600 w-6'
                        : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-72 h-72 bg-white/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-4">
              Ready to Transform Your{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                Financial Future?
              </span>
            </h2>
            <p className="text-lg mb-6 text-blue-100">
              Join 15,420+ investors who are already building wealth with YesTimeEarn's 
              advanced investment platform
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
              <Link
                to="/auth/register"
                className="group bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-gray-900 px-8 py-4 rounded-xl font-semibold text-lg inline-flex items-center gap-3 transition-all transform hover:scale-105 shadow-lg hover:shadow-yellow-500/25"
              >
                <TrendingUp className="w-5 h-5" />
                Start Investing Today
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              
              <Link
                to="/contact"
                className="border-2 border-white/50 hover:border-white text-white px-6 py-4 rounded-xl font-semibold text-lg inline-flex items-center gap-2 transition-all backdrop-blur-sm hover:bg-white/10"
              >
                <Users className="w-5 h-5" />
                Talk to Our Experts
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <div className="text-xl font-bold text-yellow-400">24/7</div>
                <div className="text-blue-100 text-sm">Support</div>
              </div>
              <div>
                <div className="text-xl font-bold text-green-400">99.9%</div>
                <div className="text-blue-100 text-sm">Uptime</div>
              </div>
              <div>
                <div className="text-xl font-bold text-emerald-400">256-bit</div>
                <div className="text-blue-100 text-sm">SSL Security</div>
              </div>
              <div>
                <div className="text-xl font-bold text-orange-400">Instant</div>
                <div className="text-blue-100 text-sm">Withdrawals</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

export default Home